import React, { Fragment } from 'react';
import { LiveCasinoThumbnail } from '../../../verticals/src/components/GameThumbnails/LiveCasino/LiveCasinoThumbnail';
import { CasinoThumbnail } from '../../../verticals/src/components/GameThumbnails/Casino/CasinoThumbnail';
import { LiveCasinoService } from './live-casino.service';
import { GridItem } from '../../../verticals/src/components/Grid/GridItem';
import { StringUtilService } from './util/string-util.service';
import JackpotThumbnail from '../../../verticals/src/components/GameThumbnails/JackpotThumbnail';
import { verticalTypesEnum } from '../enums/vertical-types.enum';

export const GamesService = {
    LOW_BALANCE_AMOUNT: 10,
    SIZE_BIG: 300,
    SIZE_MEDIUM: 255,
    SIZE_SMALL: 190,
    SIZE_CLASS_BIG: 'big',
    SIZE_CLASS_MEDIUM: 'medium',
    SIZE_CLASS_SMALL: 'small',
    SIZE_CLASS_XSMALL: 'xsmall',

    /**
     * Based on game object - determines what thumbnail component should be rendered
     * e.g. casino, live-casino etc
     */
    getGameThumbnailComponent({
        game,
    }) {
        const gameProvider = game?.isCmsGame ? game.externalGameProvider : game.externalGameSubProvider;
        if (LiveCasinoService.isEvolutionGame(gameProvider)) {
            return {
                GameThumbnail: LiveCasinoThumbnail,
                props: this.getLiveCasinoThumbnailProps(arguments[0])
            };
        }

        return {
            GameThumbnail: CasinoThumbnail,
            props: this.getCasinoThumbnailProps(arguments[0]),
        };
    },

    /**
     * Returns thumbnail size name based on item's width
     * @param {number} thumbnailWidth
     * @param {number} thumbnailHeight
     * @returns {string}
     */
    getThumbnailSizeClass(thumbnailWidth, thumbnailHeight) {
        const useHeightCheck = thumbnailWidth / thumbnailHeight >= 3;
        const { SIZE_BIG, SIZE_MEDIUM, SIZE_SMALL, SIZE_CLASS_BIG,
            SIZE_CLASS_MEDIUM, SIZE_CLASS_SMALL, SIZE_CLASS_XSMALL } = GamesService;

        if (thumbnailWidth >= SIZE_BIG) {
            if (useHeightCheck) {
                return SIZE_CLASS_MEDIUM;
            }

            return SIZE_CLASS_BIG;
        }

        if (thumbnailWidth > SIZE_MEDIUM) {
            if (useHeightCheck) {
                return SIZE_CLASS_XSMALL;
            }

            return SIZE_CLASS_MEDIUM;
        }

        if (thumbnailWidth >= SIZE_SMALL) {
            if (useHeightCheck) {
                return SIZE_CLASS_XSMALL;
            }

            return SIZE_CLASS_SMALL;
        }

        return SIZE_CLASS_XSMALL;
    },

    /**
     * Returns banner size name based on item's width
     * @param bannerWidth
     * @returns {string}
     */
    getBannerSizeClass(bannerHeight) {
        if (bannerHeight > 328) {
            return 'big';
        }

        if (bannerHeight > 125) {
            return 'medium';
        }

        return 'small';
    },

    /**
     * Build props for CasinoThumbnail component
     */
    getCasinoThumbnailProps({
        game,
        AsgFW,
        Dictionary,
        loadGameAction,
        goToAction,
        isLoggedIn,
        isMobile,
        recentGames,
        regulation,
        buyWinExcludedSubProviders,
        dispatch,
    }) {
        const props = {
            id: game.id,
            name: game.name,
            images: {
                default: game.images.default,
                webp: game.images.webp,
                horizontalRectangleImage: game.images.horizontalRectangleImage,
                verticalRectangleImage: game.images.verticalRectangleImage,
            },
            badges: [],
            overlayBadges: {
                left: null,
                right: null,
            },
            openGameHandler: () => {
                dispatch(loadGameAction({
                    gameId: game.id
                }));
            },
            isMobile,
        };

        /** badges **/
        // BuyWin
        if (AsgFW.isDEregulation() && GamesService.isBuyWinGame(
            game.externalGameProvider,
            game.externalGameSubProvider,
            buyWinExcludedSubProviders,
        )) {
            props.badges.push({
                badgeName: 'buywin',
                badgeText: Dictionary.translate('BuyWin'),
            });
        }

        // Played or New
        if (isLoggedIn && Array.isArray(recentGames) && recentGames.includes(game.id)) {
            props.badges.push({
                badgeName: 'played',
                badgeText: Dictionary.translate('played'),
            });
        } else if (game.gameConfig.includes('New')) {
            props.badges.push({
                badgeName: 'new',
                badgeText: Dictionary.translate('newBanner'),
            });
        }

        /** overlay badges **/
        // BuyWin
        if (AsgFW.isDEregulation() && GamesService.isBuyWinGame(
            game.externalGameProvider,
            game.externalGameSubProvider,
            buyWinExcludedSubProviders,
        )) {
            props.overlayBadges.left = {
                badgeName: 'buywin',
                badgeText: Dictionary.translate('aboutBuywin'),
                onClickHandler: () => { dispatch(goToAction('/promotions/buywin')); },
            };
        }

        // Info icon
        if (GamesService.isInfoIconAllowed(isLoggedIn, regulation)) {
            props.overlayBadges.right = {
                badgeName: 'info',
                badgeText: Dictionary.translate('information'),
                onClickHandler: () => { dispatch(goToAction(GamesService.getPreLoginGameUrl(game.name))); },
            };
        }

        return props;
    },

    /**
     * Build props for LiveCasinoThumbnail component
     */
    getLiveCasinoThumbnailProps({
        game,
        Dictionary,
        isLoggedIn,
        isMobile,
        recentGames,
        liveCasinoData,
        currencyCode,
        currencySign,
        loadGameAction,
        dispatch,
    }) {
        const gameCode = GamesService.getGameSlug(game.name);
        const betLimits = liveCasinoData?.betLimits?.[gameCode] ?? null;
        const liveCasinoState = liveCasinoData?.state?.[gameCode] ?? null;

        return {
            id: game.id,
            name: game.name,
            images: {
                default: game.images.default,
                webp: game.images.webp,
                horizontalRectangleImage: game.images.horizontalRectangleImage,
                verticalRectangleImage: game.images.verticalRectangleImage,
            },
            badges: LiveCasinoService.getGridItemBadges({
                Dictionary,
                isLoggedIn,
                game,
                recentGames,
                betLimits,
                liveCasinoState,
                currencyCode,
                currencySign,
            }),
            openGameHandler: () => {
                dispatch(loadGameAction({
                    gameId: game.id
                }));
            },
            isMobile,
            liveCasinoState,
        };
    },

    isBuyWinGame(providerName, subProviderName, buyWinExcludedSubProviders) {
        const isPariPlay = providerName.toLowerCase() === 'pariplay';

        if (subProviderName && buyWinExcludedSubProviders) {
            return isPariPlay && !buyWinExcludedSubProviders.includes(subProviderName);
        }

        return isPariPlay;
    },

    /**
     * Determines if we can render info icons for games
     */
    isInfoIconAllowed(isLoggedIn, regulation) {
        if (isLoggedIn) {
            return false;
        }

        return ['MGA', 'DK'].includes(regulation.toUpperCase());
    },

    /**
     * Replaces spaces with dashes, trims and puts to lower case passed game name
     * @param gameName
     * @return {string}
     */
    getGameSlug(gameName) {
        return gameName.trim().replaceAll(' ', '-').toLowerCase();
    },

    /**
     * Builds pre login URL for game
     * @param gameName
     * @returns {string}
     */
    getPreLoginGameUrl(gameName) {
        const slug = gameName
            .trim()
            .replace(/[ ]/g, '-')
            .replace(/'/g, '')
            .toLowerCase();

        return `/casino/games/${slug}`;
    },

    renderCasinoThumbnails(params) {
        const isPremium = params.AsgFW.getRbcSettings('intJackpotsPremium')
            || params.AsgFW.getRbcSettings('jackpotTickerNg');
        const jackpotsIdsByDate = params.jackpotsByDate ? [...params.jackpotsByDate]: null;
        return params.gamesToRender.map((gameId, index) => this.renderCasinoThumbnail({
            index,
            gameId,
            ...params,
            isPremium,
            jackpotsByDate: jackpotsIdsByDate,
        }));
    },

    renderCasinoThumbnail({
        index,
        gameId,
        AsgFW,
        currentVerticalId,
        verticalsById,
        regulation,
        Currency,
        CurrencySign,
        liveCasinoData,
        session,
        isMobile,
        recentGames,
        loadGame,
        goTo,
        buyWin,
        dispatch,
        Dictionary,
        countColumnsCallback,
        jackpotsByDate,
        jackpots,
        isPremium,
        isFavoritesCategory,
    }) {
        const game = AsgFW.getGameById(gameId);
        const isThumbnailAvailable = this.isPositionForJackpotThumbnail(index);
        const jackpot = jackpots && isThumbnailAvailable && this.findJackpot(jackpotsByDate, Currency, jackpots);
        const isLiveCasino = verticalsById?.[currentVerticalId].type === verticalTypesEnum.LIVE_CASINO;

        if (!game) {
            return null;
        }

        // detect what thumbnail component should be rendered based on game object
        const { GameThumbnail, props } = this.getGameThumbnailComponent({
            game,
            AsgFW,
            regulation,
            currencyCode: Currency,
            currencySign: CurrencySign,
            Dictionary,
            liveCasinoData,
            isLoggedIn: !!session,
            isMobile,
            recentGames,
            loadGameAction: loadGame,
            goToAction: goTo,
            buyWinExcludedSubProviders: buyWin?.excludedSubProviders,
            dispatch,
        });

        return <Fragment key={gameId}>
            {isThumbnailAvailable && jackpot && isPremium && !isLiveCasino && !isFavoritesCategory && <GridItem
                countColumnsCallback={index === 0 ? countColumnsCallback : null}
            >
                <JackpotThumbnail jackpot={jackpot} />
            </GridItem>}
            <GridItem
                countColumnsCallback={index === 0 ? countColumnsCallback : null}
            >
                <GameThumbnail {...props} />
            </GridItem>
        </Fragment>;
    },

    isPositionForJackpotThumbnail(gridItemIndex) {
        const jpThumbnailEveryXGames = 7;

        return gridItemIndex !== 0 && gridItemIndex % jpThumbnailEveryXGames === 0;
    },

    findJackpot(jackpotsIdsByDate, Currency, jackpots) {
        let jackpot;
        if (!jackpotsIdsByDate || !jackpots) {
            return;
        }
        do {
            const jackpotId = jackpotsIdsByDate.shift();
            jackpot = jackpots[jackpotId];
            let amount = jackpot && jackpot?.amount[Currency];
            if (amount && jackpot.showInGrid) {
                return jackpot;
            }
        } while (jackpotsIdsByDate.length > 0);
    },

    getGameProviderName(game) {
        return game.externalGameSubProvider || game.externalGameProvider;
    },

    sortAtoZ(list) {
        return list.sort((a, b) => (a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })));
    },

    groupGames(gamesList, titleType = null) {
        return this.sortAtoZ(
            Object.entries(
                gamesList.reduce((titles, game) => {
                    const title = titleType ? this.getGameProviderName(game) : game.name.trim().charAt(0).toUpperCase();

                    return {
                        ...titles,
                        [title]: [
                            ...(titles[title] ?? []),
                            titleType ? {
                                ...game,
                                name: StringUtilService.capitalize(game.name)
                            }
                                : {
                                    ...game,
                                    provider: this.getGameProviderName(game),
                                    name: StringUtilService.capitalize(game.name)
                                }
                        ],
                    };
                }, {}),
            ).map(([name, games]) => ({
                name,
                games: this.sortAtoZ(games)
            }))
        );
    },

    hasDemoMode(gameItem) {
        return gameItem.playModes.includes('Demo') && !AsgFW.isLoggedIn() && AsgFW.getRbcSettings('demoGames');
    },

    filterGamesToRender(games, AsgFW) {
        return games.map(
            gameId => AsgFW.getGameById(gameId) ? gameId : null
        ).filter(Boolean);
    },

    replaceGameName(str, gameId, gamesById) {
        const game = gameId ? gamesById?.[gameId] : {};
        const gameName = game ? game?.name : '';

        return StringUtilService.replaceAll(str, '[gameName]', gameName || '');
    },

    isLowBalance(balanceInCoins) {
        return Number(balanceInCoins) / 100 < this.LOW_BALANCE_AMOUNT;
    },
};
